import {
  AlertTriangle,
  CheckCircle,
  Globe,
  HelpCircle,
  Lock,
  XCircle,
} from 'lucide-react';
import type { TabSecurityInfo, TabSecurityStatus } from '@/components/tabs/tabSecurity';

const STATUS_CONFIG: Record<
  TabSecurityStatus,
  { icon: typeof CheckCircle; color: string; label: string }
> = {
  safe: { icon: CheckCircle, color: 'text-green-400 bg-green-500/20', label: 'Safe' },
  warning: { icon: AlertTriangle, color: 'text-yellow-400 bg-yellow-500/20', label: 'Warning' },
  danger: { icon: XCircle, color: 'text-red-400 bg-red-500/20', label: 'Danger' },
  unscanned: { icon: HelpCircle, color: 'text-khiimori-text-muted bg-khiimori-bg-elevated/20', label: 'Not scanned' },
};

function scoreBarColor(score: number): string {
  return score >= 80 ? 'bg-green-500' : score >= 50 ? 'bg-yellow-500' : 'bg-red-500';
}

export function SecurityScoreBar({ score, compact }: { score: number | null; compact?: boolean }) {
  if (score === null) {
    return (
      <span className={`text-khiimori-text-muted ${compact ? 'text-xs' : 'text-sm'}`} title="Not yet scanned">
        —
      </span>
    );
  }
  const barColor = scoreBarColor(score);
  return (
    <div className={`flex items-center gap-2 ${compact ? '' : 'min-w-[8rem]'}`}>
      <div className={`flex-1 bg-khiimori-bg-elevated/10 rounded-full h-2 ${compact ? 'w-16' : 'w-24'}`}>
        <div className={`h-2 rounded-full ${barColor}`} style={{ width: `${Math.min(100, Math.max(0, score))}%` }} />
      </div>
      <span className={`font-medium text-khiimori-text-primary ${compact ? 'text-xs' : 'text-sm'}`}>{score}</span>
    </div>
  );
}

export function SecurityStatusBadge({ status }: { status: TabSecurityStatus }) {
  const config = STATUS_CONFIG[status];
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${config.color}`}>
      <Icon className="w-3.5 h-3.5" />
      {config.label}
    </span>
  );
}

export function EncryptionBadge({ encryption }: { encryption: boolean }) {
  return encryption ? (
    <span className="inline-flex items-center gap-1 text-sm text-green-400">
      <Lock className="w-4 h-4" />
      HTTPS
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-sm text-red-400">
      <Globe className="w-4 h-4" />
      HTTP
    </span>
  );
}

export function ThreatsLabel({ threats }: { threats: string[] }) {
  if (threats.length === 0) {
    return <span className="text-sm text-khiimori-text-secondary">None</span>;
  }
  return (
    <span className="text-sm text-red-400" title={threats.join(', ')}>
      {threats.length} detected
    </span>
  );
}

export function TabSecuritySummaryStrip({
  totalTabs,
  safeTabs,
  warningTabs,
  totalThreats,
  onScanAll,
  isScanning,
}: {
  totalTabs: number;
  safeTabs: number;
  warningTabs: number;
  totalThreats: number;
  onScanAll: () => void;
  isScanning: boolean;
}) {
  return (
    <div className="mb-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
      <div className="rounded-xl border border-khiimori-border/30 bg-khiimori-bg-elevated/30 px-4 py-3">
        <p className="text-xs text-khiimori-text-muted">Total tabs</p>
        <p className="text-2xl font-bold text-khiimori-text-primary">{totalTabs}</p>
      </div>
      <div className="rounded-xl border border-khiimori-border/30 bg-khiimori-bg-elevated/30 px-4 py-3">
        <p className="text-xs text-khiimori-text-muted">Safe</p>
        <p className="text-2xl font-bold text-green-400">{safeTabs}</p>
      </div>
      <div className="rounded-xl border border-khiimori-border/30 bg-khiimori-bg-elevated/30 px-4 py-3">
        <p className="text-xs text-khiimori-text-muted">Warning</p>
        <p className="text-2xl font-bold text-yellow-400">{warningTabs}</p>
      </div>
      <div className="rounded-xl border border-khiimori-border/30 bg-khiimori-bg-elevated/30 px-4 py-3">
        <p className="text-xs text-khiimori-text-muted">Findings</p>
        <p className="text-2xl font-bold text-red-400">{totalThreats}</p>
      </div>
      <button
        type="button"
        onClick={onScanAll}
        disabled={isScanning || totalTabs === 0}
        className="rounded-xl border border-primary-500/30 bg-primary-500/10 px-4 py-3 text-left hover:bg-primary-500/15 transition-colors disabled:opacity-50"
      >
        <p className="text-xs text-primary-300">{isScanning ? 'Scanning…' : 'Security scan'}</p>
        <p className="text-sm font-semibold text-primary-200 mt-1">Scan all tabs</p>
      </button>
    </div>
  );
}

export type { TabSecurityInfo };
